import React, { useEffect, useState } from 'react'
import LeftBar from '../Components/LeftBar'
import BottomNav from '../Components/BottomNav'
import Topbar from '../Components/Topbar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSun, faMoon, faCloudSun } from '@fortawesome/free-solid-svg-icons'
import { app } from "../firebase.js"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { doc, getDoc, getFirestore, collection, query, where, getDocs } from "firebase/firestore"
import { useNavigate } from 'react-router-dom'
import dashImage from '../Images/dashone.svg'
import dashb from '../Images/dashb1.png'
import VerificationStatus from './VerificationStatus.js'

function Dashboard() {
  const auth = getAuth()
  const db = getFirestore(app)
  const navigate = useNavigate()

  const [username, setUsername] = useState("Hey Stranger")
  const [userId, setUserId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accountBalance, setAccountBalance] = useState(0)
  const [adsStats, setAdsStats] = useState({ total: 0, totalPaid: 0, totalPending: 0 })
  const [subsStats, setSubsStats] = useState({ total: 0, totalPaid: 0, totalPending: 0 })
  const [runningAdsStats, setRunningAdsStats] = useState({ running: 0, thisMonth: 0 })

  useEffect(() => {
    document.title = "Zindua App | Influencer Dashboard";

    // Meta description
    let desc = document.querySelector("meta[name='description']");
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", "Zindua App influencer dashboard. View your earnings, ad campaigns, subscriptions, and withdraw funds to M-Pesa.");

    // Open Graph tags
    const ogTags = [
      { property: "og:title", content: "Zindua App | Influencer Dashboard" },
      { property: "og:description", content: "View your earnings, running ads, subscription stats, and withdraw funds safely on Zindua App." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://zindua.app/dashboard" },
      { property: "og:image", content: "https://zindua.app/logo192.png" },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property='${property}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    });

    // JSON-LD structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Zindua App Influencer Dashboard",
      "url": "https://zindua.app/dashboard",
      "description": "Zindua App influencer dashboard where you can manage your earnings, running ads, subscriptions, and withdraw funds to M-Pesa.",
      "inLanguage": "en",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://zindua.app/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Dashboard",
            "item": "https://zindua.app/dashboard"
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Greeting
  const now = new Date()
  const hour = now.getHours()
  let greeting = ""
  let icon = faSun
  if (hour < 12) greeting = "Good morning"
  else if (hour < 17) { greeting = "Good afternoon"; icon = faCloudSun }
  else { greeting = "Good evening"; icon = faMoon }

  // Fetch user info
  useEffect(() => {
    const cached = JSON.parse(localStorage.getItem("profileData") || "{}")
    if (cached.userName) setUsername(cached.userName)

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return
      setUserId(user.uid)

      const snap = await getDoc(doc(db, "users", user.uid))
      if (!snap.exists()) return

      const data = snap.data()
      const freshName = data.userName || ""
      const freshImage = data.profileImage

      const changedName = cached.userName !== freshName
      const changedImage = cached.profileImage !== freshImage



      if (changedName) setUsername(freshName)
      if (changedName || changedImage) {
        localStorage.setItem("profileData", JSON.stringify({ ...cached, userName: freshName }))
      }
    })

    return () => unsubscribe()
  }, [auth, db])

  // Fetch stats and account balance
  useEffect(() => {
    if (!userId) return

    const fetchData = async () => {
      setLoading(true)
      try {
        const incomeSnap = await getDocs(query(collection(db, "income"), where("influencerId", "==", userId)))
        const incomeData = incomeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))

        // Account balance = Paid income - Withdrawals
        const totalPaidIncome = incomeData.filter(i => i.payoutStatus === "Paid").reduce((acc, i) => acc + Number(i.payout || 0), 0)
        const withdrawalsSnap = await getDocs(query(collection(db, "withdrawals"), where("userId", "==", userId)))
        const totalWithdrawn = withdrawalsSnap.docs.map(d => d.data()).reduce((acc, w) => acc + Number(w.amount || 0), 0)
        setAccountBalance(totalPaidIncome - totalWithdrawn)

        // Ads stats
        const adsIncome = incomeData.filter(i => i.incomeType === "adRequest")
        const totalAds = adsIncome.length
        const totalAdsPaid = adsIncome.filter(i => i.payoutStatus === "Paid").reduce((acc, i) => acc + Number(i.payout || 0), 0)
        const totalAdsPending = adsIncome.filter(i => i.payoutStatus === "Pending").reduce((acc, i) => acc + Number(i.payout || 0), 0)
        setAdsStats({ total: totalAds, totalPaid: totalAdsPaid, totalPending: totalAdsPending })

        // Subscription stats
        const subsIncome = incomeData.filter(i => i.incomeType === "subscription")
        const totalSubs = subsIncome.length
        const totalSubsPaid = subsIncome.filter(i => i.payoutStatus === "Paid").reduce((acc, i) => acc + Number(i.payout || 0), 0)
        const totalSubsPending = subsIncome.filter(i => i.payoutStatus === "Pending").reduce((acc, i) => acc + Number(i.payout || 0), 0)
        setSubsStats({ total: totalSubs, totalPaid: totalSubsPaid, totalPending: totalSubsPending })

        // Running Ads stats
        const adsSnap = await getDocs(query(collection(db, "ads"), where("influencerId", "==", userId)))
        const adsData = adsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))

        const runningAds = adsData.filter(ad => ad.adStatus === "Running").length
        const runningAdsThisMonth = adsData.filter(ad => {
          if (!ad.timestamp?.toDate) return false
          const date = ad.timestamp.toDate()
          return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()
        }).length

        setRunningAdsStats({ running: runningAds, thisMonth: runningAdsThisMonth })


      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [userId, db])

  const handleWithdraw = () => navigate("/withdraw")

  return (
    <div className="mainDashboard">
      <div className="desktopOnly">
        <LeftBar />
      </div>
      <div className="mainContent">
        <Topbar />

        <section className="userDash">

          <VerificationStatus />

          <div className="userDashOne">
            <div className="userDashOneWelcome">
              <FontAwesomeIcon icon={icon} className="greetIcon" />
              <h3>{greeting}</h3>
              <h6>{username}</h6>
              <img src={dashImage} alt="" />
            </div>

            <div className="userDashOneTwo">
              <div className='userDashOneTwoTop'>
                <div>
                  <h6>My Earnings</h6>
                  <p>My Account Balance</p>
                </div>
                <button onClick={handleWithdraw}>Withdraw</button>
              </div>

              <h4>KES. {(accountBalance || 0).toLocaleString()}</h4>
              <img src={dashb} alt="" />
            </div>
          </div>

          <div className="userDashTwo">

            <div className="dashBox box1color">
              <p className='box1colorTitle'>Ads Earning Overview</p>
              <div className='dashBoxRoundCont'>
                <div className='dashBoxRound'>
                  <h3>{adsStats.total || 0}</h3>
                  <p>Total Ads</p>
                </div>
              </div>
              <div className='box1colorBottom'>
                <p>Total Earnings KES {(adsStats.totalPaid || 0).toLocaleString()}</p>
                <p>Pending payments KES {(adsStats.totalPending || 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="dashBox box2color">
              <p className='box1colorTitle'>Subscription Earnings</p>
              <div className='dashBoxRoundCont'>
                <div className='dashBoxRound'>
                  <h3>{subsStats.total || 0}</h3>
                  <p>Subscribers</p>
                </div>
              </div>
              <div className='box1colorBottom'>
                <p>Total Earnings KES {(subsStats.totalPaid || 0).toLocaleString()}</p>
                <p>Pending payments KES {(subsStats.totalPending || 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="dashBox box3color">
              <p className='box1colorTitle'>Ads Distributions</p>
              <div className='dashBoxRoundCont'>
                <div className='dashBoxRound'>
                  <h3>{runningAdsStats.running || 0}</h3>
                  <p>Running Ads</p>
                </div>
              </div>
              <div className='box1colorBottom'>
                <p>Running Ads {runningAdsStats.running || 0}</p>
                <p>This Month {runningAdsStats.thisMonth || 0}</p>
              </div>
            </div>

          </div>
        </section>
      </div>
      <div className="mobileOnly">
        <BottomNav />
      </div>
    </div>
  )
}

export default Dashboard